import { useState, useEffect } from 'react';
import { Form, Typography, Alert, message } from 'antd';
import CustomButton from '../../components/CustomButton/Button';
import PasswordInput from '../../components/Auth/PasswordInput';
import { useNavigate } from 'react-router-dom';
import { changePassword, clearAuthError, logoutUser } from "../../redux/slices/AuthSlice";
import { useDispatch, useSelector } from "react-redux";
import { passwordRules } from '../../helpers/helpers';
import { BTN_TXT, TITLE_TXT } from '../../constants/constants';
import BackButton from '../../components/CustomButton/BackButton';
import CryptoJS from 'crypto-js';

const { Title } = Typography;

const ChangePassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(false);
    const { passChangeLoading, passChangeError } = useSelector((state) => state.auth);

    useEffect(() => {
        return () => {
            dispatch(clearAuthError());
        };
    }, [dispatch]);

    const toggleVisibility = () => {
        setVisible(!visible);
    };

    const onFinish = async (values) => {
        const oldhHashedPassword = CryptoJS.SHA256(values.oldPassword).toString();
        const newHashedPassword = CryptoJS.SHA256(values.newPassword).toString();
        const confirmHashedPassword = CryptoJS.SHA256(values.confirmPassword).toString();

        if (values.newPassword !== values.confirmPassword) {
            message.error("Passwords do not match!");
            return;
        }

        const payload = {
            oldPassword:oldhHashedPassword,
            newPassword: newHashedPassword,
            confirmPassword:confirmHashedPassword,
        }; 

        const res = await dispatch(changePassword(payload));

        if (res?.payload?.statusCode === 201) {
            message.success("Your password has been changed successfully! You’ve been logged out — log in again with your new password.");

            await dispatch(logoutUser());

            setTimeout(() => {
                navigate('/login');
            }, 2000);
        }
    };

    return (
        <>
            <BackButton />

            <div className="change-password-container">
                <div className="forgot-password-card-container">
                    <Title level={3}>{TITLE_TXT.CHNG_PWD_TITLE}</Title>

                    <Form
                        layout="vertical"
                        className="forgot-form"
                        onFinish={onFinish}
                        initialValues={{ remember: true }}
                    >
                        <PasswordInput
                            label="Enter Old Password"
                            name="oldPassword"
                            visible={visible}
                            onToggleVisibility={toggleVisibility}
                            placeholder="Enter old password"
                            rules={passwordRules}
                        />

                        <PasswordInput
                            label="Enter New Password"
                            name="newPassword"
                            visible={visible}
                            onToggleVisibility={toggleVisibility}
                            placeholder="Enter new password"
                            rules={passwordRules}
                        />

                        <PasswordInput
                            label="Confirm New Password"
                            name="confirmPassword"
                            visible={visible}
                            onToggleVisibility={toggleVisibility}
                            placeholder="Re-enter new password"
                            dependencies={['newPassword']}
                            rules={[
                                {
                                    required: true,
                                    message: "Please confirm your new password!",
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('newPassword') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Passwords do not match!'));
                                    },
                                }),
                            ]}
                        />

                        {passChangeError && (
                            <Alert
                                style={{ marginTop: 20 }}
                                message={passChangeError}
                                type="error"
                                showIcon
                            />
                        )}

                        <Form.Item>
                            <CustomButton
                                type="primary"
                                htmlType="submit"
                                loading={passChangeLoading}
                                block
                                style={{ marginTop: 10, padding: 24 }}
                            >
                                {BTN_TXT.SUBMIT}
                            </CustomButton>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </>
    );
};

export default ChangePassword;
